import {useCallback, useEffect, useRef, useSyncExternalStore} from 'react'

/**
 * SSR-safe React hook tracking whether the viewport currently matches `query`.
 *
 * Uses `useSyncExternalStore` so the server snapshot is always `false` and the
 * client reconciles after hydration without a mismatch. A media query listener
 * fires only when a breakpoint is crossed, unlike a `ResizeObserver` which
 * reacts to every layout change.
 *
 * @param query - The media query to match, e.g. `'(max-width: 768px)'`.
 * @param onChange - Optional callback invoked with the new match state whenever
 *   the media query result changes.
 * @returns `true` when the media query currently matches, otherwise `false`.
 */
export function useMediaQuery(query: string, onChange?: (matches: boolean) => void): boolean {
  const onChangeRef = useRef(onChange)

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const subscribe = useCallback(
    (notify: () => void) => {
      const mql = window.matchMedia(query)
      const handleChange = () => {
        notify()
        onChangeRef.current?.(mql.matches)
      }
      mql.addEventListener('change', handleChange)
      return () => {
        mql.removeEventListener('change', handleChange)
      }
    },
    [query],
  )

  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query])

  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}
